import { Bounds, Vector } from '@ardoq/graph';

import { clamp } from 'lodash';
import {
  AQBLOCKS_ALIGNMENT_LINEDASH,
  AQBLOCKS_ALIGNMENT_SIZE,
  AQBLOCKS_ALIGNMENT_STROKE,
  AQBLOCKS_BADDROP_ALPHA,
  AQBLOCKS_BADDROP_FILL,
  AQBLOCKS_GOODDROP_ALPHA,
} from '../consts';
import {
  AqBlocks,
  BlocksEditedEvent,
  BlocksNode,
  BlocksVisualState,
} from '../types';
import { NodeHit, RenderParameters } from '../visual/types';
import { getColPositions, getRowPositions } from '../placeandroute/util';
import {
  rectHeight,
  rectIntersects,
  rectWidth,
} from 'tabview/visualizations/misc/geometry';
import { interp } from 'tabview/visualizations/misc/math';
import { getDragImage } from '../visual/dragimage';
import {
  editCursor,
  forceNodeCell,
  forceVisualState,
  getColIndex,
  getRowIndex,
  isRoutingChannel,
  verifyCell,
} from './util';
import {
  renderNodeHandles,
  renderNodeHandlesShadows,
} from '../visual/node.handles';
import { DragHandler } from './types';
import { placeAndRoute } from '../placeandroute/placeandroute';
import { stripGrid } from '../layout/util';

const setLayout = (
  u: BlocksNode,
  col: number,
  row: number,
  colSpan: number,
  rowSpan: number
) => {
  u.layoutData = { col, row, colSpan, rowSpan };
};

export class EditDragHandler implements DragHandler {
  public get cursor(): string {
    return editCursor(this.itemHit);
  }

  public constructor(component: AqBlocks, item: BlocksNode, itemHit: NodeHit) {
    this.component = component;
    this.node = item;
    this.pointerDown = component.pointerDown;
    this.bounds = [...item.bounds];
    this.handles = [...item.bounds];
    this.image = getDragImage(component.projection, this.node);
    this.itemHit = itemHit;
    this.isGood = true;

    forceVisualState(this.node, BlocksVisualState.Hidden, true);
  }

  private readonly component: AqBlocks;
  private readonly node: BlocksNode;
  private readonly pointerDown: Vector = [NaN, NaN];
  private readonly itemHit: NodeHit;

  private bounds: Bounds = [NaN, NaN, NaN, NaN]; // not really sure
  private handles: Bounds = [NaN, NaN, NaN, NaN]; // world handles rectangle (so not snapped at all)
  private drop: Bounds | null = null; // world rectangle of the dropped bounds (if good)
  private real: number[][] = [[], []];
  private fake: number[][] = [[], []];
  private isGood = false;
  private image: OffscreenCanvas | null = null;

  private insert: Vector = [NaN, NaN];
  private posn: Vector = [NaN, NaN];
  private span: Vector = [NaN, NaN];

  private move() {
    const resolution = 1;
    const slop = 10;
    const node = this.node;
    const group = node.parent;

    if (!group) {
      return;
    }

    const { pointer, pointerDown } = this.component;
    const cols = getColPositions(group);
    const rows = getRowPositions(group);
    const delta = [pointer[0] - pointerDown[0], pointer[1] - pointerDown[1]]; // world move

    this.handles = [
      cols[node.layoutData!.col] + delta[0],
      rows[node.layoutData!.row] + delta[1],
      cols[node.layoutData!.col + node.layoutData!.colSpan] + delta[0],
      rows[node.layoutData!.row + node.layoutData!.rowSpan] + delta[1],
    ];

    this.bounds = this.handles;
    this.drop = [...node.bounds];

    // const center = rectCenter(this.bounds); // base things on where the center of the node is
    const center = pointer; // base things on where the center of the node is
    const col = clamp(getColIndex(group, center[0]), 0, cols.length - 2);
    const row = clamp(getRowIndex(group, center[1]), 0, rows.length - 2);

    const cell: Bounds = [
      node.layoutData!.col,
      node.layoutData!.row,
      node.layoutData!.col + node.layoutData!.colSpan - 1,
      node.layoutData!.row + node.layoutData!.rowSpan - 1,
    ];

    if (Math.abs(delta[1] / resolution) < slop) {
      this.real[1].push(rows[node.layoutData!.row]);
      this.real[1].push(rows[node.layoutData!.row + node.layoutData!.rowSpan]);
    } else {
      this.posn[1] = row;
      this.span[1] = 1;

      cell[1] = row;
      cell[3] = row;

      if (isRoutingChannel(row)) {
        this.insert[1] = row;

        const top = interp(0.15, rows[row], rows[row + 1]);
        const bottom = interp(0.85, rows[row], rows[row + 1]);

        this.fake[1].push(top);
        this.fake[1].push(bottom);

        this.drop[1] = top;
        this.drop[3] = bottom;
      } else {
        const top = rows[row];
        const bottom = rows[row + 1];

        this.real[1].push(top);
        this.real[1].push(bottom);

        this.drop[1] = top;
        this.drop[3] = bottom;
      }
    }

    if (Math.abs(delta[0] / resolution) < slop) {
      this.real[0].push(cols[node.layoutData!.col]);
      this.real[0].push(cols[node.layoutData!.col + node.layoutData!.colSpan]);
    } else {
      this.posn[0] = col;
      this.span[0] = 1;

      cell[0] = col;
      cell[2] = col;

      if (isRoutingChannel(col)) {
        this.insert[0] = col;

        const left = interp(0.15, cols[col], cols[col + 1]);
        const right = interp(0.85, cols[col], cols[col + 1]);

        this.fake[0].push(left);
        this.fake[0].push(right);

        this.drop[0] = left;
        this.drop[2] = right;
      } else {
        const left = cols[col];
        const right = cols[col + 1];

        this.real[0].push(left);
        this.real[0].push(right);

        this.drop[0] = left;
        this.drop[2] = right;
      }
    }

    if (!(rectIntersects(group.bounds, this.handles) || !group.parent)) {
      this.isGood = false;
      this.real = [[], []];
      this.fake = [[], []];
    }

    this.isGood = this.isGood && verifyCell(node, cell);

    if (!this.isGood) {
      this.drop = null;
    }
  }

  private resize() {
    const hit = this.itemHit;
    const node = this.node;
    const pointer = this.component.pointer;
    const pointerDown = this.component.pointerDown;
    const group = node.parent;
    const delta = [pointer[0] - pointerDown[0], pointer[1] - pointerDown[1]];

    this.handles = [
      node.bounds[0] + (hit & NodeHit.Left ? delta[0] : 0),
      node.bounds[1] + (hit & NodeHit.Top ? delta[1] : 0),
      node.bounds[2] + (hit & NodeHit.Right ? delta[0] : 0),
      node.bounds[3] + (hit & NodeHit.Bottom ? delta[1] : 0),
    ];

    if (!group?.children || group.children.length === 0) {
      return;
    }

    const cell: Bounds = [
      node.layoutData!.col,
      node.layoutData!.row,
      node.layoutData!.col + node.layoutData!.colSpan - 1,
      node.layoutData!.row + node.layoutData!.rowSpan - 1,
    ];

    const rightBottom = (hit: NodeHit) => {
      const dimension =
        hit & NodeHit.Right ? 0 : hit & NodeHit.Bottom ? 1 : NaN;

      if (isNaN(dimension)) {
        return;
      }

      const positions =
        dimension === 0 ? getColPositions(group) : getRowPositions(group);

      let channel =
        dimension === 0
          ? getColIndex(group, pointer[dimension])
          : getRowIndex(group, pointer[dimension]);

      channel = clamp(channel, this.posn[dimension], positions.length - 2);

      const sz = positions[channel + 1] - positions[channel];
      const p = (pointer[dimension] - positions[channel]) / sz;

      if (isRoutingChannel(channel) && p < 0.25) {
        const c = channel; // c is this routing channel

        this.span[dimension] = c - this.posn[dimension];
        this.real[dimension].push(positions[c]);
        this.bounds[2 + dimension] = positions[c];
        cell[2 + dimension] = c; // range to check for bad
      }

      if (!isRoutingChannel(channel) && p >= 0.75) {
        const c = channel + 1; // c is the routing channel

        this.span[dimension] = c - this.posn[dimension];
        this.real[dimension].push(positions[c]);
        this.bounds[2 + dimension] = positions[c];
        cell[2 + dimension] = c; // range to check for bad
      }

      if (isRoutingChannel(channel) && p >= 0.25) {
        const c = channel; // c is this routing channel

        this.insert[dimension] = c;
        this.span[dimension] = c - this.posn[dimension] + 1;
        this.fake[dimension].push(positions[c]);
        this.fake[dimension].push(positions[c + 1]);
        this.bounds[2 + dimension] = 0.5 * (positions[c] + positions[c + 1]);
        cell[2 + dimension] = c; // range to check for bad
      }

      if (!isRoutingChannel(channel) && p < 0.25) {
        const c = channel;

        this.insert[dimension] = c;
        this.span[dimension] = c - this.posn[dimension] + 1;
        this.fake[dimension].push(positions[c]);
        this.fake[dimension].push(positions[c + 1]);
        this.bounds[2 + dimension] = 0.5 * (positions[c] + positions[c + 1]);
        cell[2 + dimension] = c; // range to check for bad
      }

      if (!isRoutingChannel(channel) && p >= 0.25 && p < 0.75) {
        const c = channel;

        this.insert[dimension] = c;
        this.span[dimension] = c - this.posn[dimension] + 1;
        this.fake[dimension].push(positions[c]);
        this.fake[dimension].push(positions[c + 1]);
        this.bounds[2 + dimension] = 0.5 * (positions[c] + positions[c + 1]);
        cell[2 + dimension] = c; // range to check for bad
      }
    };

    const leftTop = (hit: NodeHit) => {
      const dimension = hit & NodeHit.Left ? 0 : hit & NodeHit.Top ? 1 : NaN;

      if (isNaN(dimension)) {
        return;
      }

      const positions =
        dimension === 0 ? getColPositions(group) : getRowPositions(group);

      let channel =
        dimension === 0
          ? getColIndex(group, pointer[dimension])
          : getRowIndex(group, pointer[dimension]);

      channel = clamp(
        channel,
        0,
        this.posn[dimension] + this.span[dimension] - 1
      );

      const sz = positions[channel + 1] - positions[channel];
      const p = (pointer[dimension] - positions[channel]) / sz;

      if (isRoutingChannel(channel) && p < 0.5) {
        const c = channel; // split routing channel

        this.insert[dimension] = c;
        this.span[dimension] =
          this.posn[dimension] + this.span[dimension] - c + 1;
        this.posn[dimension] = c + 1;

        this.fake[dimension].push(positions[c]);
        this.fake[dimension].push(positions[c + 1]);
        this.bounds[dimension] = 0.5 * (positions[c] + positions[c + 1]);
        cell[dimension] = c; // range to check for bad
      }

      if (isRoutingChannel(channel) && p >= 0.5) {
        const c = channel + 1; // snap to start of node channel above

        this.span[dimension] = this.posn[dimension] + this.span[dimension] - c;
        this.posn[dimension] = c;
        this.real[dimension].push(positions[c]);
        this.bounds[dimension] = positions[c];
        cell[dimension] = c; // range to check for bad
      }

      if (!isRoutingChannel(channel) && p < 0.5) {
        const c = channel; // snap down to start of node channel

        this.span[dimension] = this.posn[dimension] + this.span[dimension] - c;
        this.posn[dimension] = c;
        this.real[dimension].push(positions[c]);
        this.bounds[dimension] = positions[c];
        cell[dimension] = c; // range to check for bad
      }

      if (!isRoutingChannel(channel) && p >= 0.5) {
        const c = channel; // split node channel

        this.insert[dimension] = c;
        this.span[dimension] = this.posn[dimension] + this.span[dimension] - c;
        this.posn[dimension] = c + 2;
        this.fake[dimension].push(positions[c]);
        this.fake[dimension].push(positions[c + 1]);
        this.bounds[dimension] = 0.5 * (positions[c] + positions[c + 1]);
        cell[dimension] = c; // range to check for bad
      }
    };

    rightBottom(hit & NodeHit.Right);
    rightBottom(hit & NodeHit.Bottom);
    leftTop(hit & NodeHit.Left);
    leftTop(hit & NodeHit.Top);

    this.isGood = this.isGood && verifyCell(node, cell);
  }

  public drag() {
    this.component.autoPan();
    this.component.pendRender();

    /* initial stuff before anything happens */

    const node = this.node;

    this.isGood = true;
    this.insert = [NaN, NaN];
    this.posn = [node.layoutData!.col, node.layoutData!.row];
    this.span = [node.layoutData!.colSpan, node.layoutData!.rowSpan];

    this.bounds = [
      node.bounds[0],
      node.bounds[1],
      node.bounds[2],
      node.bounds[3],
    ];

    this.handles = [
      node.bounds[0],
      node.bounds[1],
      node.bounds[2],
      node.bounds[3],
    ];

    this.drop = null;
    this.real = [[], []];
    this.fake = [[], []];

    if (this.itemHit & NodeHit.Body) {
      /* move */
      this.move();
    } else {
      /* resize */
      this.resize();
    }
  }

  public render(params: RenderParameters) {
    const { ctx, projection } = params;
    const group = this.node.parent;

    if (!group) {
      return;
    }

    if (this.image) {
      /* draw the node at the handles position with red overlay if bad */
      const rc = projection.projectBounds(this.handles);

      renderNodeHandlesShadows(rc, params);

      if (this.isGood) {
        ctx.globalAlpha = AQBLOCKS_GOODDROP_ALPHA;
        ctx.drawImage(this.image, rc[0], rc[1], rectWidth(rc), rectHeight(rc));
      } else {
        ctx.globalAlpha = AQBLOCKS_BADDROP_ALPHA;
        ctx.fillStyle = AQBLOCKS_BADDROP_FILL;
        ctx.fillRect(rc[0], rc[1], rectWidth(rc), rectHeight(rc));
        ctx.drawImage(this.image, rc[0], rc[1], rectWidth(rc), rectHeight(rc));
      }

      ctx.globalAlpha = 1;
      renderNodeHandles(rc, params);
    }

    // #region draw real alignment lines
    const groupBounds = group.parent ? group.bounds : projection.window;

    ctx.save();

    ctx.beginPath();

    for (const col of this.real[0]) {
      ctx.moveTo(...projection.projectPoint([col, groupBounds[1]]));
      ctx.lineTo(...projection.projectPoint([col, groupBounds[3]]));
    }

    for (const row of this.real[1]) {
      ctx.moveTo(...projection.projectPoint([groupBounds[0], row]));
      ctx.lineTo(...projection.projectPoint([groupBounds[2], row]));
    }

    ctx.lineWidth = AQBLOCKS_ALIGNMENT_SIZE;
    ctx.strokeStyle = AQBLOCKS_ALIGNMENT_STROKE;
    ctx.setLineDash([]);
    ctx.stroke();

    /* draw fake alignment lines */

    ctx.beginPath();

    for (const col of this.fake[0]) {
      ctx.moveTo(...projection.projectPoint([col, groupBounds[1]]));
      ctx.lineTo(...projection.projectPoint([col, groupBounds[3]]));
    }

    for (const row of this.fake[1]) {
      ctx.moveTo(...projection.projectPoint([groupBounds[0], row]));
      ctx.lineTo(...projection.projectPoint([groupBounds[2], row]));
    }

    ctx.lineWidth = AQBLOCKS_ALIGNMENT_SIZE;
    ctx.strokeStyle = AQBLOCKS_ALIGNMENT_STROKE;
    ctx.setLineDash(AQBLOCKS_ALIGNMENT_LINEDASH);
    ctx.stroke();

    ctx.restore();
    //# endregion
  }

  public done() {
    if (this.isGood && this.component.graph) {
      forceVisualState(this.node, BlocksVisualState.Hidden, false);
      forceNodeCell(this.node, this.bounds, false);

      const node = this.node;
      const group = node.parent;
      const siblings = group && group.children ? group.children : null;

      if (!group || !siblings) {
        return;
      }

      group.layoutType = 'manual';

      /* TODO UNDO COMMENTED OUT FOR INCREMENTAL PRS
        const dirtyNodes = new Map<BlocksViewNode, EntryItem>();
      */

      const shuffleColumn = (col: number) => {
        for (const u of siblings) {
          if (
            u !== node &&
            u.layoutData!.col <= col &&
            u.layoutData!.col + u.layoutData!.colSpan > col
          ) {
            setLayout(
              u,
              u.layoutData!.col,
              u.layoutData!.row,
              u.layoutData!.colSpan + 2,
              u.layoutData!.rowSpan
            );
            continue;
          }

          if (u !== node && u.layoutData!.col > col) {
            setLayout(
              u,
              u.layoutData!.col + 2,
              u.layoutData!.row,
              u.layoutData!.colSpan,
              u.layoutData!.rowSpan
            );
            continue;
          }
        }
      };

      const shuffleRow = (row: number) => {
        for (const u of siblings) {
          if (
            u !== node &&
            u.layoutData!.row <= row &&
            u.layoutData!.row + u.layoutData!.rowSpan > row
          ) {
            setLayout(
              u,
              u.layoutData!.col,
              u.layoutData!.row,
              u.layoutData!.colSpan,
              u.layoutData!.rowSpan + 2
            );
            continue;
          }

          if (u !== node && u.layoutData!.row > row) {
            setLayout(
              u,
              u.layoutData!.col,
              u.layoutData!.row + 2,
              u.layoutData!.colSpan,
              u.layoutData!.rowSpan
            );
            continue;
          }
        }
      };

      if (!isNaN(this.insert[0])) {
        /* insert columns */
        shuffleColumn(this.insert[0]);
      }

      if (!isNaN(this.insert[1])) {
        /* insert rows */
        shuffleRow(this.insert[1]);
      }

      /* set the node position and span */

      setLayout(node, this.posn[0], this.posn[1], this.span[0], this.span[1]);
      stripGrid(group, setLayout); /*  strip dead columns and rows */

      /* TODO UNDO COMMENTED OUT FOR INCREMENTAL PRS
      const entry: Entry = [];

      for (const entryItem of dirtyNodes.values()) {
        entry.push(entryItem);
      }

      if (entry.length !== 0) {
        stack.push(entry);
      }
      */

      placeAndRoute(
        this.component.graph,
        this.component.layoutStyle.linksAggregation,
        this.component.placementStyle
      );

      this.component.pendRender();
      this.component.dispatchEvent(
        new BlocksEditedEvent({ graph: this.component.graph })
      );
    } else {
      this.cancel();
    }
  }

  public cancel() {
    forceVisualState(this.node, BlocksVisualState.Hidden, false);

    if (this.node.cell) {
      forceNodeCell(this.node, this.bounds, true);
    }
  }
}
